'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

// 商品数据接口
interface GoodItemProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: any[];
  [key: string]: any;
}

interface GoodItemWapProps {
  product: GoodItemProduct;
  index: number;
  onProductClick?: (product: GoodItemProduct, index: number) => void;
  onProductExp?: (product: GoodItemProduct, index: number) => void;
}

export default function GoodItemWap({
  product,
  index,
  onProductClick,
  onProductExp,
}: GoodItemWapProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const t = useTranslations('product');

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onProductExp?.(product, index);
    }
  }, [inView]);

  // 判断是否售罄
  const isSoldOut = product.stock <= 0;

  // 获取商品图片
  const productImg = (item: GoodItemProduct) => {
    const list = item?.image?.product || [];
    return list[0] || '';
  };

  // 处理商品点击
  const toDetail = (item: GoodItemProduct, itemIndex: number) => {
    // 触发点击回调
    onProductClick?.(item, itemIndex);

    // 跳转到商品详情页
    if (item?.shop_sku) {
      navigate(`/goods-detail/${item.shop_sku}`);
    }
  };

  return (
    <a
      href={`/goods-detail/${product.shop_sku}`}
      onClick={(e) => {
        e.preventDefault();
        toDetail(product, index);
      }}
    >
      <div
        ref={expRef}
        className="product-info bg-[#EEEEEE] rounded-[8px] overflow-hidden relative w-full h-[150px] flex flex-col"
      >
        {/* 图片容器 - 占据主要空间 */}
        <div className="image-wrapper flex-1 px-[28px] pt-[16px] pb-[4px] flex items-center justify-center overflow-hidden">
          <Image
            src={productImg(product)}
            alt={`${product.name || ''}`}
            width={600}
            height={600}
            className="black-friday-bottom-image max-w-full max-h-full object-contain mix-blend-multiply"
          />
        </div>

        {/* 商品信息区域 - 固定底部空间 */}
        <div className="product-details pt-[4px] px-[12px] pb-[8px] flex-shrink-0">
          {/* 商品名称 */}
          <div className="promotion-list-item-name text-[12px] text-[#808080] mb-[4px] text-center line-clamp-1">
            {product.short_name}
          </div>

          {/* 价格信息 */}
          <div className="promotion-list-item-price flex justify-center">
            <div className="promotion-list-item-price-text">
              {(() => {
                const p = priceInfo(userInfo, product, currency) || {};
                const now = formatPriceNumberWithSymbol(
                  String(p?.sell_price || p?.price || '0'),
                  currency,
                );
                const old =
                  p && p.price && p.sell_price && p.sell_price !== p.price
                    ? formatPriceNumberWithSymbol(String(p.price), currency)
                    : '';
                return (
                  <>
                    <span className="now-price notranslate text-[14px] text-[#282828] font-bb">
                      {now}
                    </span>
                    {old && (
                      <span className="old-price notranslate text-[10px] text-[#A9A9A9] line-through ml-[4px]">
                        {old}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* 售罄遮罩 */}
        {isSoldOut && (
          <div className="absolute inset-0 bg-white/40 rounded-[8px] cursor-default">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[27%] aspect-square rounded-full bg-black/60 text-center">
              <div
                className="text-white whitespace-pre-wrap text-[12px]"
                dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
              />
            </span>
          </div>
        )}
      </div>
    </a>
  );
}
